import React, { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Progress,
  Row,
  Spinner,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  saCompleteTenantMigrationCutover,
  saGetTenantMigrationStatus,
  saGetTenantDetails,
  saStartTenantMigration,
} from "store/actions/superAdminAction"
import { showAlert } from "store/reducers/alertReducer"
import ConfirmModal from "components/Common/ConfirmModal"

const MIGRATION_STATUS_COLORS = {
  idle: "secondary",
  queued: "info",
  running: "warning",
  completed: "success",
  failed: "danger",
}

const isActiveMigration = status =>
  status === "queued" || status === "running"

const TenantDatabaseMigrationPanel = ({ tenantId, tenantDetails = {} }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { tenantMigrationStatus, tenantMigrationLoading, tenantMigrationActionLoading } =
    useSelector(state => state.SuperAdmin)

  const [showCutoverModal, setShowCutoverModal] = useState(false)

  const hasSeparateDb = Boolean(tenantDetails?.hasSeparateDb)
  const database = tenantDetails?.database || {}
  const statusPayload = tenantMigrationStatus?.tenantId === tenantId
    ? tenantMigrationStatus
    : null
  const migration = statusPayload?.migration || database?.migration || {}
  const migrationStatus = migration?.status || "idle"
  const cutoverComplete = Boolean(
    statusPayload?.cutoverComplete ?? database?.cutoverComplete
  )
  const databaseStatus = statusPayload?.databaseStatus || database?.status

  const fetchStatus = useCallback(() => {
    if (!tenantId || !hasSeparateDb) return
    dispatch(saGetTenantMigrationStatus({ id: tenantId }))
  }, [dispatch, tenantId, hasSeparateDb])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  useEffect(() => {
    if (!tenantId || !hasSeparateDb || !isActiveMigration(migrationStatus)) {
      return undefined
    }

    const timer = setInterval(fetchStatus, 3000)
    return () => clearInterval(timer)
  }, [tenantId, hasSeparateDb, migrationStatus, fetchStatus])

  if (!hasSeparateDb) {
    return null
  }

  const handleStartMigration = () => {
    dispatch(saStartTenantMigration({ id: tenantId })).then(res => {
      if (res?.payload?.status) {
        dispatch(
          showAlert({
            type: "success",
            message: t("Data migration started in background"),
          })
        )
        fetchStatus()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.payload?.msg || t("Unable to start data migration"),
          })
        )
      }
    })
  }

  const handleCutover = () => {
    dispatch(saCompleteTenantMigrationCutover({ id: tenantId })).then(res => {
      setShowCutoverModal(false)
      if (res?.payload?.status) {
        dispatch(
          showAlert({
            type: "success",
            message: t("Tenant cutover completed successfully"),
          })
        )
        dispatch(saGetTenantDetails({ id: tenantId }))
        fetchStatus()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.payload?.msg || t("Unable to complete cutover"),
          })
        )
      }
    })
  }

  const canStartMigration =
    databaseStatus === "active" &&
    !cutoverComplete &&
    !isActiveMigration(migrationStatus) &&
    migrationStatus !== "completed"

  const canCompleteCutover =
    databaseStatus === "active" &&
    migrationStatus === "completed" &&
    !cutoverComplete

  return (
    <>
      <Card className="mt-3">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-1">{t("Dedicated database migration")}</h5>
              <p className="text-muted mb-0 small">
                {cutoverComplete
                  ? t("Tenant is live on the dedicated database.")
                  : t(
                      "Until cutover completes, the tenant continues using the shared database."
                    )}
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Badge color={MIGRATION_STATUS_COLORS[migrationStatus] || "secondary"}>
                {t("Migration")}: {t(migrationStatus || "idle")}
              </Badge>
              <Badge color={cutoverComplete ? "success" : "warning"}>
                {cutoverComplete ? t("Cutover complete") : t("Cutover pending")}
              </Badge>
            </div>
          </div>

          {tenantMigrationLoading && !migration?.totalCollections ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
            </div>
          ) : (
            <>
              {database?.databaseName ? (
                <p className="small text-muted mb-2">
                  {t("Database")}: <code>{database.databaseName}</code>
                </p>
              ) : null}

              {isActiveMigration(migrationStatus) || migrationStatus === "completed" ? (
                <>
                  <div className="mb-2 d-flex justify-content-between small">
                    <span>
                      {migration.currentCollection
                        ? `${t("Current collection")}: ${migration.currentCollection}`
                        : migrationStatus === "completed"
                          ? t("All collections migrated")
                          : t("Preparing migration...")}
                    </span>
                    <span>{migration.progressPercent || 0}%</span>
                  </div>
                  <Progress
                    value={migration.progressPercent || 0}
                    color={
                      migrationStatus === "failed"
                        ? "danger"
                        : migrationStatus === "completed"
                          ? "success"
                          : "primary"
                    }
                    className="mb-3"
                  />
                  <Row className="g-2 small text-muted mb-3">
                    <Col md={4}>
                      {t("Collections")}: {migration.completedCollections || 0}/
                      {migration.totalCollections || 0}
                    </Col>
                    <Col md={4}>
                      {t("Documents copied")}: {migration.migratedDocuments || 0}
                    </Col>
                    <Col md={4}>
                      {t("DB status")}: {databaseStatus || "—"}
                    </Col>
                  </Row>
                </>
              ) : null}

              {migrationStatus === "failed" && migration.error ? (
                <Alert color="danger" className="py-2">
                  {migration.error}
                </Alert>
              ) : null}

              {!cutoverComplete && databaseStatus === "active" ? (
                <Alert color="info" className="py-2 mb-3">
                  {t(
                    "Integration catalogue and billing records stay on the platform database. Tenant operational data is copied to the dedicated database."
                  )}
                </Alert>
              ) : null}

              <div className="d-flex gap-2 flex-wrap">
                {canStartMigration ? (
                  <Button
                    color="primary"
                    size="sm"
                    disabled={tenantMigrationActionLoading}
                    onClick={handleStartMigration}
                  >
                    {tenantMigrationActionLoading ? (
                      <Spinner size="sm" />
                    ) : migrationStatus === "failed" ? (
                      t("Retry migration")
                    ) : (
                      t("Start data migration")
                    )}
                  </Button>
                ) : null}

                {canCompleteCutover ? (
                  <Button
                    color="success"
                    size="sm"
                    disabled={tenantMigrationActionLoading}
                    onClick={() => setShowCutoverModal(true)}
                  >
                    {t("Complete cutover")}
                  </Button>
                ) : null}

                <Button
                  color="secondary"
                  outline
                  size="sm"
                  disabled={tenantMigrationLoading}
                  onClick={fetchStatus}
                >
                  {t("Refresh status")}
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmModal
        show={showCutoverModal}
        onCloseClick={() => setShowCutoverModal(false)}
        onDeleteClick={handleCutover}
        buttonText={t("Complete cutover")}
        bodyTitle={t(
          "Switch this tenant to the dedicated database now? Make sure migration completed successfully before cutover."
        )}
        isLoading={tenantMigrationActionLoading}
      />
    </>
  )
}

export default TenantDatabaseMigrationPanel
